import React from "react";
import classNames from 'classnames';
import { user } from '../../../../../user';
import { IColumnDefinition } from "../../../../bidding/common/table/types/ColumnDefinition";
import { ColumnBuilder } from "../../../../bidding/common/table/columns/column-builder/ColumnBuilder";
import { constants, roles } from "../../../../../constants";
import { amrFormatUtils, formatUtils, moneyUtils, numericUtils } from "../../../../../utils";
import { OriginatingTransactionClass } from "../../../../../types/amr-pipeline/models/OriginatingTransactionClass";
import { OnHoverTooltip } from "../../../../common";
import { OverlayClassNameContent } from "../../../common/OverlayClassNameContent";
import { constants as amrConstants } from "../../../common/constants";
import { OriginatingTransactionClassStatus, transactionClassStatusTitles } from "../../../../../types/amr-pipeline/enums/OriginatingTransactionClassStatus";
import { LastUpdatedPopover } from '../../LastUpdatedPopover';
import { RequiredFeature } from "../../../../access/RequiredFeature";
import { SubscriptionFeature } from "../../../../../types/billing/SubscriptionFeature";
import { BloombergLink } from "../../../../common/BloombergLink";
import { AmrTransaction } from "../../../../../types/amr-pipeline/models/AmrTransaction";
import { OriginatingTransaction } from "../../../../../types/amr-pipeline/models/OriginatingTransaction";
import { ValitanaLink } from "../../../../common/ValitanaLink";
import { ImSubscriptionValue } from '../../../subscription/ImSubscriptionValue';
import { BwicMonitorLink } from "../../../common/BwicMonitorLink";
import { BwicInventoryLink } from "../../../common/BwicInventoryLink";
import { AddToPortfolioLink } from "../../../common/AddToPortfolioLink";

export const newIssueColumns = () => {
    const isAdminOrDataEntryOrMedia = user.hasRoles(roles.Administrator, roles.DataEntry, roles.Media);

    const statsColumnDefinitions: IColumnDefinition<OriginatingTransactionClass>[] = [
        {
            columnKey: "className",
            renderColumnHeaderContent: () => "Class",
            renderColumnContent: (transactionClass) => (
                <OnHoverTooltip
                    overlay={
                        <OverlayClassNameContent
                            transactionClass={transactionClass}
                        />
                    }
                >
                    <span className="text-ellipsis">
                        {`${transactionClass.name} ${
                            transactionClass.originatingTransactionClassStatus ===
                            OriginatingTransactionClassStatus.NotOffered
                                ? amrConstants.notOffered
                                : ""
                        }`}
                    </span>
                </OnHoverTooltip>
            ),
            className: "data-list-cell-xs"
        },
        {
            columnKey: "rating",
            renderColumnHeaderContent: () => "Rtg (M/S/F/K/D)",
            renderColumnContent: (transactionClass) =>
                amrFormatUtils.formatRating(transactionClass),
            className: "data-list-cell-md",
        },
        {
            columnKey: "balance",
            renderColumnHeaderContent: () => "Balance",
            renderColumnContent: (transactionClass) =>
                transactionClass.balance != null && numericUtils.isNumber(transactionClass.balance)
                    ? moneyUtils.money(transactionClass.balance)
                    : constants.emptyPlaceholder,
            className: "data-list-cell-sm-02 text-right",
        },
        {
            columnKey: "parSubordination",
            renderColumnHeaderContent: () => "C/E, %",
            renderColumnContent: (transactionClass) => (
                <ImSubscriptionValue>
                    {
                        transactionClass.parSubordination
                            ? formatUtils.formatDecimal(
                                transactionClass.parSubordination
                            )
                            : constants.emptyPlaceholder
                    }
                </ImSubscriptionValue>
            ),
            className: "data-list-cell-xs-sm text-right",
        },
        {
            columnKey: "mvoc",
            renderColumnHeaderContent: () => "MVOC, %",
            renderColumnContent: (transactionClass) => (
                <ImSubscriptionValue>
                    {
                        transactionClass.mvoc
                            ? formatUtils.formatDecimal(transactionClass.mvoc)
                            : constants.emptyPlaceholder
                    }
                </ImSubscriptionValue>
            ),
            className: "text-right",
        },
        {
            columnKey: "wal",
            renderColumnHeaderContent: () => "WAL",
            renderColumnContent: (transactionClass) => (
                <ImSubscriptionValue>
                    {
                        transactionClass.wal
                            ? formatUtils.formatDecimal(transactionClass.wal)
                            : constants.emptyPlaceholder
                    }
                </ImSubscriptionValue>
            ),
            className: "data-list-cell-xs text-right",
        },
        {
            columnKey: "coupon",
            renderColumnHeaderContent: () => "Coupon",
            renderColumnContent: ({ floaterIndex, margin}) =>
                <ImSubscriptionValue>
                    {amrFormatUtils.formatInventoryCoupon(floaterIndex, margin)}
                </ImSubscriptionValue>,
            className: "data-list-cell-md",
        },
        {
            columnKey: "dm",
            renderColumnHeaderContent: () => "DM, bps",
            renderColumnContent: (transactionClass) =>
                <ImSubscriptionValue>
                    {transactionClass.dm ?? constants.emptyPlaceholder}
                </ImSubscriptionValue>,
            className: "data-list-cell-xs-sm text-right",
        },
        {
            columnKey: "initialPrice",
            renderColumnHeaderContent: () =>  <span>Initial Price</span>,
            renderColumnContent: (transactionClass) => (
                <ImSubscriptionValue>
                    { transactionClass.initialPrice ?
                        <OnHoverTooltip overlay={transactionClass.initialPrice}>
                            <span className="text-ellipsis">{transactionClass.initialPrice}</span>
                        </OnHoverTooltip>
                        : constants.emptyPlaceholder
                    }
                </ImSubscriptionValue>
            ),
            className: "data-list-cell-sm-02 text-right",
        },
        {
            columnKey: "price",
            renderColumnHeaderContent: () => "Price",
            renderColumnContent: (transactionClass) => (
                <ImSubscriptionValue>
                    { transactionClass.price ?
                        <OnHoverTooltip overlay={transactionClass.price}>
                            <span className="text-ellipsis">{transactionClass.price}</span>
                        </OnHoverTooltip>
                        : constants.emptyPlaceholder
                    }
                </ImSubscriptionValue>
            ),
            className: "data-list-cell-xs text-right",
        },
        {
            columnKey: "initialGuidance",
            renderColumnHeaderContent: () =>  <span>Initial Guidance</span>,
            renderColumnContent: (transactionClass) => (
                <ImSubscriptionValue>
                    { transactionClass.initialGuidance ?
                        <OnHoverTooltip overlay={transactionClass.initialGuidance}>
                            <span className="text-ellipsis">{transactionClass.initialGuidance}</span>
                        </OnHoverTooltip>
                        : constants.emptyPlaceholder
                    }
                </ImSubscriptionValue>
            ),
            className: "data-list-cell-sm",
        },
        {
            columnKey: "guidance",
            renderColumnHeaderContent: ({ transaction } : {
                transaction: AmrTransaction | OriginatingTransaction
            }) => {
                const withGuidance = transaction.classes.some(c => c.guidance);
                return withGuidance ? (
                    <RequiredFeature
                        feature={SubscriptionFeature.IssuanceMonitorFullAccess}
                        renderBlockedContent={() => 'Guidance'}
                    >
                        <LastUpdatedPopover
                            transaction={transaction}
                            field="guidance"
                        >
                            <span className="tooltip-wrapper pseudo-link">
                                Guidance
                            </span>
                        </LastUpdatedPopover>
                    </RequiredFeature>
                ) : (
                    <span>Guidance</span>
                )
            },
            renderColumnContent: (transactionClass) => (
                <ImSubscriptionValue>
                    { transactionClass.guidance ?
                        <OnHoverTooltip overlay={transactionClass.guidance}>
                            <span className="text-ellipsis">{transactionClass.guidance}</span>
                        </OnHoverTooltip>
                        : constants.emptyPlaceholder
                    }
                 </ImSubscriptionValue>
            ),
            className: "",
        },
        {
            columnKey: "originatingTransactionClassStatus",
            renderColumnHeaderContent: () => "Class Status",
            renderColumnContent: (transactionClass) =>
                transactionClass.originatingTransactionClassStatus
                    ? transactionClassStatusTitles[transactionClass.originatingTransactionClassStatus]
                    : constants.emptyPlaceholder,
            className: "data-list-cell-sm-01",
        },
        {
            columnKey: "subscription",
            renderColumnHeaderContent: ({ transaction }: {
                transaction: AmrTransaction | OriginatingTransaction
            }) => {
                const withSubscription = transaction.classes.some(c => (c as OriginatingTransactionClass).subscription);
                return withSubscription ? (
                    <RequiredFeature
                        feature={SubscriptionFeature.IssuanceMonitorFullAccess}
                        renderBlockedContent={() => 'Subscription'}
                    >
                        <LastUpdatedPopover
                            transaction={transaction}
                            field="subscription"
                        >
                            <span className="tooltip-wrapper pseudo-link">
                                Subscription
                            </span>
                        </LastUpdatedPopover>
                    </RequiredFeature>
                ) : (
                    <span>Subscription</span>
                )
            },
            renderColumnContent: (transactionClass) => (
                <ImSubscriptionValue>
                    { transactionClass.subscription ?
                        <OnHoverTooltip overlay={transactionClass.subscription}>
                            <span className="text-ellipsis">{transactionClass.subscription}</span>
                        </OnHoverTooltip>
                        : constants.emptyPlaceholder
                    }
                </ImSubscriptionValue>
            ),
            className: "data-list-cell-sm-01",
        },
        {
            columnKey: "target",
            renderColumnHeaderContent: () => "O/C Target, %",
            renderColumnContent: (transactionClass) => (
                <ImSubscriptionValue>
                    {
                        formatUtils.formatDecimal(transactionClass.ocTarget)
                        || constants.emptyPlaceholder
                    }
                </ImSubscriptionValue>
            ),
            className: "data-list-cell-sm text-right",
        },
        {
            columnKey: "trigger",
            renderColumnHeaderContent: () => "O/C Trigger, %",
            renderColumnContent: (transactionClass) => (
                <ImSubscriptionValue>
                    {
                        formatUtils.formatDecimal(transactionClass.ocTrigger) ||
                        constants.emptyPlaceholder
                    }
                </ImSubscriptionValue>
            ),
            className: "data-list-cell-sm text-right",
        },
        {
            columnKey: "cushion",
            renderColumnHeaderContent: () => "O/C Cushion, %",
            renderColumnContent: (transactionClass) => (
                <ImSubscriptionValue>
                    {
                        transactionClass.ocCushion
                            ? formatUtils.formatDecimal(transactionClass.ocCushion)
                            : constants.emptyPlaceholder
                    }
                </ImSubscriptionValue>
            ),
            className: "data-list-cell-sm text-right",
        },
        {
            columnKey: "ticker",
            renderColumnHeaderContent: () => "Ticker (144A)",
            renderColumnContent: (transactionClass) =>
                    transactionClass.tranche?.ticker144A || constants.emptyPlaceholder,
            className: "data-list-cell-lg",
        },
        {
            columnKey: "cusip",
            renderColumnHeaderContent: () => "Identifier 1 (144A)",
            renderColumnContent: (transactionClass) =>
                transactionClass.tranche?.cusip144A || constants.emptyPlaceholder,
            className: "data-list-cell-md",
        },
        {
            columnKey: "isin",
            renderColumnHeaderContent: () => "Identifier 2 (144A)",
            renderColumnContent: (transactionClass) =>
                transactionClass.tranche?.isin144A || constants.emptyPlaceholder,
            className: "data-list-cell-md",
        },
        {
            columnKey: "tickerRegS",
            renderColumnHeaderContent: () => "Ticker (Reg S)",
            renderColumnContent: (transactionClass) =>
                transactionClass.tranche?.tickerRegS || constants.emptyPlaceholder,
            className: "data-list-cell-lg",
        },
        {
            columnKey: "cusipRegS",
            renderColumnHeaderContent: () => "Identifier 1 (Reg S)",
            renderColumnContent: (transactionClass) =>
                transactionClass.tranche?.cusipRegS || constants.emptyPlaceholder,
            className: "data-list-cell-md",
        },
        {
            columnKey: "isinRegS",
            renderColumnHeaderContent: () => "Identifier 2 (Reg S)",
            renderColumnContent: (transactionClass) =>
                transactionClass.tranche?.isinRegS || constants.emptyPlaceholder,
            className: "data-list-cell-md",
        },
        {
            columnKey: "tickerAccdInvCertif",
            renderColumnHeaderContent: () => "Ticker (Acc’d Inv./Certif.)",
            renderColumnContent: (transactionClass) =>
                transactionClass.tranche?.tickerAccdInvCertif || constants.emptyPlaceholder,
            className: "data-list-cell-lg-02",
        },
        {
            columnKey: "cusipAccdInvestor",
            renderColumnHeaderContent: () => "Identifier 1 (Acc’d Inv./Certif.)",
            renderColumnContent: (transactionClass) =>
                transactionClass.tranche?.cusipAccdInvCertif || constants.emptyPlaceholder,
            className: "data-list-cell-lg-02",
        },
        {
            columnKey: "isinAccdInvestor",
            renderColumnHeaderContent: () => "Identifier 2 (Acc’d Inv./Certif.)",
            renderColumnContent: (transactionClass) =>
                transactionClass.tranche?.isinAccdInvCertif || constants.emptyPlaceholder,
            className: "data-list-cell-lg-02",
        },
    ];

    const actionsColumn: IColumnDefinition<OriginatingTransactionClass>[] = [
        {
            columnKey: 'emptyCol',
            renderColumnHeaderContent: () => '',
            renderColumnContent: () => '',
            className: 'data-list-cell-xxxs',
            stickRight: true,
        },
        {
            columnKey: 'actions',
            renderColumnHeaderContent: () => '',
            renderColumnContent: (transactionClass, context) => (
                <div className="action-row">
                    <div className="action-col" onClick={e => e.stopPropagation()}>
                        <BwicMonitorLink
                            currency={context.transaction.currency}
                            identifiers={transactionClass.tranche}
                            secondaryPlaceholderColor
                        />
                    </div>
                    <div className="action-col" onClick={e => e.stopPropagation()}>
                        <BwicInventoryLink
                            currency={context.transaction.currency}
                            identifiers={transactionClass.tranche}
                            secondaryPlaceholderColor
                        />
                    </div>
                    <div className="action-col" onClick={e => e.stopPropagation()}>
                        <AddToPortfolioLink
                            transaction={context.transaction}
                            transactionClass={transactionClass}
                            checkSubscription
                        />
                    </div>
                    <div className="action-col" onClick={e => e.stopPropagation()}>
                        <BloombergLink
                            identifiers={transactionClass.tranche}
                            placeholderIfEmpty
                            secondaryPlaceholderColor
                        />
                    </div>
                    <div className="action-col" onClick={e => e.stopPropagation()}>
                        <ValitanaLink
                            placeholderIfEmpty
                            secondaryPlaceholderColor
                            identifiers={transactionClass.tranche}
                        />
                    </div>
                </div>
            ),
            className: classNames(
                {
                    'data-list-cell-lg-02': isAdminOrDataEntryOrMedia,
                    'data-list-cell-md-flexible': !isAdminOrDataEntryOrMedia,
                },
                'data-list-cell-bloomberg-menu overflow-visible text-right',
            ),
            stickRight: true,
        },
    ];

    const middleColumns = statsColumnDefinitions.map((c) => {
        return new ColumnBuilder(c);
    });

    const rightColumns = actionsColumn.map((c) => {
        return new ColumnBuilder(c);
    });

    return [[], middleColumns, rightColumns];
};
